// externals
import { type Dispatch, memo, type SetStateAction, useEffect } from 'react';
import { useSelector } from 'react-redux';

// components
import {
    FieldUpdatingSpinner,
    usePaymentCall,
    useFieldUpdatingIndicator,
    FlexCol,
    FlexRow,
    StyledVAutoAppraisalStatus,
    dealLockHooks,
    useVAutoAppraisalLaunch
} from '@makemydeal/dr-dash-components';
import { Typography } from '@interstate/components/Typography';
import { Action } from '@interstate/components/Action';
import { ArrowTopRightOnSquareIcon } from '@interstate/components/Icons';
import type { BadgeVariant } from '@interstate/components/Badge';

import { Alert } from '@interstate/components/Alert';
import { Flag } from '@interstate/components/Flag';
import { Grid } from '@interstate/components/Grid';

// libraries
import { formatDollarsAndCents, kebabCase, formatNumberAndCents } from '@makemydeal/dr-common-utils';

// selectors
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as vAutoSelectors from '@makemydeal/dr-dash-store/selectors/vAutoSelectors';
import * as tradeActionTypes from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as vAutoTriforceSelectors from '@makemydeal/dr-dash-store/selectors/vAutoTriforceSelectors';

// constants
import { ACV, ALLOWANCE, GENERIC_LAST_FIELD_CHANGED_ERROR, PAYOFF } from '../../../constants';
import {
    SOURCE_TYPE_DEALER,
    ALLOWANCE_SOURCE_LABEL_MANAGER,
    ALLOWANCE_SOURCE_LABEL_SALESPERSON
} from '@makemydeal/dr-dash-store/constants';

import { VAutoAppraisalStatus } from '@makemydeal/dr-dash-types';
import type { LienholderFormData } from '../../../types';

import LienholderForm from '../lienholderForm/LienholderForm.interstate';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useMediaQuery } from 'react-responsive';
import { tradeInContainerGridProps } from '../lienholderForm/utils';
import type { SoftSaveLienHolderData } from '../../../types/TradeIn';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';
import { AllowanceFlagWrapper } from '../trade.interstate.style';
import TriforceWidget from '../triforceWidget/TriforceWidget';

export interface TradeInValuationProps {
    isEditDisabled: boolean;
    lienholderData: LienholderFormData;
    setLienholderData: Dispatch<SetStateAction<LienholderFormData>>;
    softSaveLienHolderData?: SoftSaveLienHolderData;
    isTradeInFormDirtyRef?: React.MutableRefObject<boolean>;
}

const shouldClearDirtyState = (tradeInFormErrors: any): boolean => {
    return !!(
        !tradeInFormErrors?.lienholder?.accountNumber?.hasError &&
        !tradeInFormErrors?.lienholder?.streetAddress?.hasError &&
        !tradeInFormErrors?.lienholder?.city?.hasError &&
        !tradeInFormErrors?.lienholder?.state?.hasError &&
        !tradeInFormErrors?.lienholder?.zip?.hasError
    );
};

const TradeInValuationComponent = ({
    isEditDisabled,
    lienholderData,
    setLienholderData,
    softSaveLienHolderData,
    isTradeInFormDirtyRef
}: TradeInValuationProps) => {
    const { tradeInValidator, tradeInIndex, tradeInFormErrors } = useSingleTradeInFormProvider();
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);

    const isLocked = dealLockHooks.useIsDealLocked();
    const tradeInputsDisabled = isLocked || isEditDisabled;

    const vin = useSelector(tradeInsSelectors.getVin(tradeInIndex));
    const isVAutoAppraisalEnabled = useSelector(dealerSelectors.isVAutoAppraisalEnabled);
    const isTradeAcvOverrideEnabled = useSelector(dealerSelectors.isTradeAcvOverrideEnabled);
    const currAppraisal = useSelector(vAutoSelectors.getCurrentAppraisalByVin(vin));
    const vAutoAppraisalStatus = currAppraisal?.status;
    const vAutoAppraisalAcv = currAppraisal?.value;
    const failedAllowance = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.ALLOWANCE_CHANGED));
    const isMobile = useMediaQuery({ query: `(max-width: ${BreakPoint.PHONE})` });
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);

    const failedPayoff = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.PAYOFF_CHANGED));

    const failedACV = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.ACV_CHANGED));

    const vAutoTriforceWidgetData = useSelector(vAutoTriforceSelectors.getVAutoTriforceDataByVin(vin));
    const vAutoTriforceScriptUrl = useSelector(configSelectors.getVAutoTriforceScriptUrl);

    const isAllowanceSourceIndicatorEnabled = useSelector(featureToggleSelectors.isAllowanceSourceIndicatorEnabled);
    const tradeSource = useSelector(tradeInsSelectors.getTradeSource(tradeInIndex));

    const isBreakdownOpen = useSelector(baseDealSelectors.getBreakdownStatus);
    const isLargeScreen = useMediaQuery({ query: `(min-width: 1360px)` });
    const isInsideTradeInSlideOut =
        useSelector(baseDealSelectors.getCurrentSlideOut) === baseDealActionCreators.SlideOutType.TradeIn;

    const { xs, sm, md, lg, xl } = tradeInContainerGridProps(
        isBreakdownOpen,
        isLargeScreen,
        isDealCentralMultiScenario,
        isInsideTradeInSlideOut
    );
    const {
        value: allowance,
        handleKeyDown: allowanceKeyDown,
        handleChange: changeAllowance,
        handleBlur: allowanceBlur
    } = usePaymentCall(
        tradeInsSelectors.getTradeValue(tradeInIndex),
        tradeInsActionCreators.allowanceChanged(tradeInIndex, isAllowanceSourceIndicatorEnabled),
        {
            formatValueToString: formatNumberAndCents,
            ignoreTextValue: () => false
        }
    );

    const {
        value: payoff,
        handleKeyDown: payoffKeyDown,
        handleChange: changePayoff,
        handleBlur: payoffBlur
    } = usePaymentCall(tradeInsSelectors.getTradeInAmountOwed(tradeInIndex), tradeInsActionCreators.payoffChanged(tradeInIndex), {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    const { actionCreator: trackedAcvAction, isUpdating: isAcvUpdating } = useFieldUpdatingIndicator(
        tradeInsActionCreators.acvChanged(tradeInIndex)
    );
    const {
        value: acv,
        handleKeyDown: acvKeyDown,
        handleChange: changeAcv,
        handleBlur: acvBlur
    } = usePaymentCall(tradeInsSelectors.getTradeACV(tradeInIndex), trackedAcvAction, {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    const { launchAppraisal, keepFocusOnMouseDown } = useVAutoAppraisalLaunch(tradeInIndex);

    // An appraisal refresh can overwrite the ACV, and that update does not come from this field.
    const isRefreshingAppraisals = useSelector(vAutoSelectors.getIsRefreshingAppraisals);
    const isAcvBusy = isAcvUpdating || isRefreshingAppraisals;

    // Deferred payoff save: When lienholder becomes valid in contracting context with pending payoff
    useEffect(() => {
        if (isContractingContext && Number(payoff) > 0) {
            if (lienholderData.lienholder?.trim()) {
                payoffBlur({ target: { value: payoff } });
                if (shouldClearDirtyState(tradeInFormErrors) && isTradeInFormDirtyRef) {
                    isTradeInFormDirtyRef.current = false;
                }
            } else {
                if (isTradeInFormDirtyRef) {
                    isTradeInFormDirtyRef.current = true;
                }
            }
        }
    }, [lienholderData.lienholder, isContractingContext]);

    const getAppraisalStatusTitle = () => {
        if (isVAutoAppraisalEnabled) {
            let variant: BadgeVariant = 'info';
            let message = '';
            let isAppraisalAvailable = false;
            const isDisabledLink = !vin || isEditDisabled;

            if (vAutoAppraisalStatus === VAutoAppraisalStatus.InProcess) {
                variant = 'caution';
                message = 'Appraisal in Progress';
                isAppraisalAvailable = true;
            } else if (vAutoAppraisalStatus === VAutoAppraisalStatus.Completed) {
                variant = 'success';
                message = 'Appraised';
                isAppraisalAvailable = true;
            }

            const acvUpdateAlert = isAppraisalAvailable && vAutoAppraisalAcv > 0 && (
                <Alert displayCloseButton data-testid="vauto-appraisal-acv-alert" role="banner" type="info">
                    <Typography variant="body-sm" tag="span">{`ACV was updated to ${formatDollarsAndCents(
                        vAutoAppraisalAcv
                    )}`}</Typography>
                </Alert>
            );

            const badge = (
                <StyledVAutoAppraisalStatus data-testid="vauto-status-badge" variant={variant}>
                    {message}
                </StyledVAutoAppraisalStatus>
            );

            const link = (
                // The mousedown sits on a wrapper because Action does not type the prop; `display:
                // contents` keeps the Action itself as the flex item so the row is unchanged.
                <span style={{ display: 'contents' }} onMouseDown={keepFocusOnMouseDown}>
                    <Action
                        data-testid="vAuto-link"
                        actionIcon={{
                            icon: <ArrowTopRightOnSquareIcon />,
                            ...(isMobile ? { end: true } : { start: true })
                        }}
                        onClick={launchAppraisal}
                        disabled={isDisabledLink}
                    >
                        <Typography
                            variant="anchor-block-sm"
                            tag={/* istanbul ignore next */ isDisabledLink ? 'div' : 'a'}
                            color={/* istanbul ignore next */ isDisabledLink ? 'base.color.gray.400' : 'base.color.blue.700'}
                        >
                            {isAppraisalAvailable ? 'View in vAuto' : 'Start Appraisal'}
                        </Typography>
                    </Action>
                </span>
            );

            return (
                <FlexCol>
                    {acvUpdateAlert}
                    <FlexRow justifyContent={isMobile ? 'start' : 'space-between'} gap="1.5rem" alignItems="center">
                        <Typography variant="h4">Valuation {isAppraisalAvailable && badge}</Typography>
                        {link}
                    </FlexRow>
                    {vAutoTriforceWidgetData && (
                        <FlexRow justifyContent="flex-end">
                            <TriforceWidget displayData={vAutoTriforceWidgetData} scriptUrl={vAutoTriforceScriptUrl} />
                        </FlexRow>
                    )}
                </FlexCol>
            );
        }

        return (
            <FlexCol>
                <FlexRow justifyContent="space-between" alignItems="center">
                    <Typography variant="h4">Valuation</Typography>
                </FlexRow>
            </FlexCol>
        );
    };

    const displayAcv = !isTradeAcvOverrideEnabled && vAutoAppraisalAcv > 0 ? formatNumberAndCents(vAutoAppraisalAcv) : acv;

    const handlePayoffBlur = (event: InterstateOnChangeEvent<string>) => {
        if (isContractingContext) {
            const payoffAmount = Number(payoff);
            tradeInValidator.setValues({ lienholder: { amountOwed: payoffAmount } }, { mode: 'merge', validate: true });
            if (payoffAmount > 0 && !lienholderData.lienholder?.trim()) {
                if (isTradeInFormDirtyRef) {
                    isTradeInFormDirtyRef.current = true;
                }
            } else {
                payoffBlur(event);
                if (shouldClearDirtyState(tradeInFormErrors) && isTradeInFormDirtyRef) {
                    isTradeInFormDirtyRef.current = false;
                }
            }
        } else {
            payoffBlur(event);
        }
    };

    return (
        <>
            <Grid
                columnSpacing={isDealCentralMultiScenario ? 1 : { xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
                container
                flexDirection="row"
                rowSpacing={1}
            >
                <Grid xs={12}>{getAppraisalStatusTitle()}</Grid>
                {/* new layouts based on the allowance source indicator - need more space for allowance input */}
                {isAllowanceSourceIndicatorEnabled ? (
                    <>
                        <Grid xs={12} sm={6} md={6} lg={lg * 2} xl={xl * 2}>
                            <AllowanceFlagWrapper>
                                {tradeSource && (
                                    <Flag
                                        variant="light"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: `calc(${ALLOWANCE.length}ch + 0.25rem)`,
                                            zIndex: 1
                                        }}
                                    >
                                        {tradeSource === SOURCE_TYPE_DEALER
                                            ? ALLOWANCE_SOURCE_LABEL_MANAGER
                                            : ALLOWANCE_SOURCE_LABEL_SALESPERSON}
                                    </Flag>
                                )}
                                <NumericInput
                                    inputPrefix={'$'}
                                    autoInsertCommas
                                    allowDecimal
                                    decimalMaxLength={2}
                                    decimalMinLength={0}
                                    data-testid="allowance"
                                    label={ALLOWANCE}
                                    name={kebabCase(ALLOWANCE)}
                                    onChange={changeAllowance}
                                    placeholder="10,000.00"
                                    value={allowance}
                                    hasError={failedAllowance}
                                    errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                                    onBlur={allowanceBlur}
                                    onKeyDown={allowanceKeyDown}
                                    disabled={tradeInputsDisabled}
                                    size="sm"
                                    selectOnFocusSelector={
                                        featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap
                                    }
                                />
                            </AllowanceFlagWrapper>
                        </Grid>
                        <Grid xs={6} sm={3} md={3} lg={lg / 2} xl={xl}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                data-testid="acv"
                                inputSuffix={isAcvBusy ? <FieldUpdatingSpinner testId="acv-updating-indicator" /> : undefined}
                                label={ACV}
                                name={kebabCase(ACV)}
                                onChange={changeAcv}
                                placeholder="10,000.00"
                                value={displayAcv}
                                onBlur={acvBlur}
                                onKeyDown={acvKeyDown}
                                hasError={failedACV}
                                errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                                disabled={tradeInputsDisabled || (!!vAutoAppraisalStatus && !isTradeAcvOverrideEnabled)}
                                size="sm"
                                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            />
                        </Grid>
                        <Grid xs={6} sm={3} md={3} lg={lg / 2} xl={xl}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                data-testid="payoff"
                                label={PAYOFF}
                                name={kebabCase(PAYOFF)}
                                onChange={changePayoff}
                                placeholder="10,000.00"
                                value={payoff}
                                hasError={failedPayoff}
                                errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                                onBlur={handlePayoffBlur}
                                onKeyDown={payoffKeyDown}
                                disabled={tradeInputsDisabled}
                                size="sm"
                                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                data-testid="allowance"
                                label={ALLOWANCE}
                                name={kebabCase(ALLOWANCE)}
                                onChange={changeAllowance}
                                placeholder="10,000.00"
                                value={allowance}
                                hasError={failedAllowance}
                                errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                                onBlur={allowanceBlur}
                                onKeyDown={allowanceKeyDown}
                                disabled={tradeInputsDisabled}
                                size="sm"
                                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            />
                        </Grid>
                        <Grid xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                data-testid="acv"
                                inputSuffix={isAcvBusy ? <FieldUpdatingSpinner testId="acv-updating-indicator" /> : undefined}
                                label={ACV}
                                name={kebabCase(ACV)}
                                onChange={changeAcv}
                                placeholder="10,000.00"
                                value={displayAcv}
                                onBlur={acvBlur}
                                onKeyDown={acvKeyDown}
                                hasError={failedACV}
                                errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                                disabled={tradeInputsDisabled || (!!vAutoAppraisalStatus && !isTradeAcvOverrideEnabled)}
                                size="sm"
                                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            />
                        </Grid>
                        <Grid xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                data-testid="payoff"
                                label={PAYOFF}
                                name={kebabCase(PAYOFF)}
                                onChange={changePayoff}
                                placeholder="10,000.00"
                                value={payoff}
                                hasError={failedPayoff}
                                errorMessage={GENERIC_LAST_FIELD_CHANGED_ERROR}
                                onBlur={handlePayoffBlur}
                                onKeyDown={payoffKeyDown}
                                disabled={tradeInputsDisabled}
                                size="sm"
                                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
            <Grid xs={12} container flexDirection="row" columnSpacing={1} rowSpacing={1}>
                <Grid container xs={12}>
                    <LienholderForm
                        isEditDisabled={isLocked || isEditDisabled}
                        lienholderData={lienholderData}
                        setLienholderData={setLienholderData}
                        softSaveLienHolderData={softSaveLienHolderData}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export const TradeInValuation = memo(TradeInValuationComponent);
